import { useState } from 'react'
import omit from 'lodash-es/omit'

import { EMPTY_OBJECT } from '../../constants/common'
import { ScreenContainerRef } from '../../context'

// Multi-entry modal host state. Each entry id keeps its own mount so opening a
// new modal in one entry does not unmount another entry's modal — required to
// keep minimized modals mounted while another modal is shown on top.
//
// `modalsConfig` maps a setter name to a `{ closer, id }` record. The
// returned object exposes:
//   - `modals`: an entries array `[[id, node], ...]` for rendering
//   - one `(value) => void` per setter name
//   - one `() => void` per closer name
//
// `modalsConfig` MUST be a stable reference (define it at module scope or via
// `useMemo`) — redefining the object every render churns the memo and creates
// fresh setters/closers each time.
//
// Example:
//
//   const EMAILS_MODAL_CONFIG = {
//     setEmailFormModal: {
//       closer: 'closeEmailFormModal',
//       id: MINIMIZED_MODAL_KEY.EMAIL_FORM,
//     },
//     setModal: { closer: 'closeModal', id: MODAL_ID_DEFAULT },
//   }
//
//   const {
//     modals,
//     setEmailFormModal,
//     closeEmailFormModal,
//     setModal,
//     closeModal,
//   } = useModals(EMAILS_MODAL_CONFIG)
export function useModals(modalsConfig = EMPTY_OBJECT) {
  const [modals, setModals] = useState(EMPTY_OBJECT)

  function setModal(id, value) {
    setModals(prev => {
      if (value == null) {
        if (!(id in prev)) {
          return prev
        }

        return omit(prev, id)
      }

      return { ...prev, [id]: value }
    })
  }

  const handlers = Object.entries(modalsConfig).reduce(
    (exposed, [setterName, { closer, id }]) => {
      exposed[setterName] = value => setModal(id, value)
      exposed[closer] = () => setModal(id, null)

      return exposed
    },
    {}
  )

  // Modals render as viewport-scale overlays through a portal, but React context
  // (including `ScreenContainerRef`) still flows from the render site. Reset it so
  // `useScreen` inside a modal measures the viewport — consistent with the CSS
  // media breakpoints — instead of whatever (possibly narrow) page container the
  // modal was opened from. A modal that needs its own container sets a deeper
  // provider, which overrides this reset.
  const modalEntries = Object.entries(modals).map(([id, node]) => [
    id,
    <ScreenContainerRef key={id} value={null}>
      {node}
    </ScreenContainerRef>,
  ])

  return { modals: modalEntries, ...handlers }
}
